<script setup lang="ts">
import {computed, ref, useTemplateRef, watch} from "vue";
import {getting} from "@/Composables/api";
import {PageLoading} from "@/Components/Global";
import {getOpengraphData} from "@/Services/stimuli";
import {Icon} from "@iconify/vue";
import Str from "@/Utils/Str";
import {register} from "swiper/element/bundle";
import Button from "@/Components/Library/Button.vue";
import Question from "@/Models/Question.ts";

const props = defineProps<{
    question?: Question;
    stimulus: any;
    points?: Array<{x: number; y: number; [key: string]: any}>;
    annotation?: any;
    pageIndex?: number;
}>();
const emit = defineEmits(["point-click", "fullwidth"]);
const showAnnotations = ref(true);
const fullWidth = ref(true);
register();
const swiperEl = useTemplateRef("swiperEl");
const imageContainer = useTemplateRef("imageContainer");
const stimulusContainer = useTemplateRef("stimulusContainer");
const ogMeta = ref(props.stimulus.ogMeta);
const pdfViewerEnabled = navigator.pdfViewerEnabled;
// console.log(props.stimulus, navigator.pdfViewerEnabled);
const getOgMeta = (refresh = false) => {
    if (refresh) {
        ogMeta.value = null;
    }
    if (props.stimulus.type.slug === "video" && props.stimulus.data) {
        let embedUrl = props.stimulus.data;
        if (props.stimulus.data.toLowerCase().includes("yout")) {
            let ytregex = new RegExp(/(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm);
            let videoId = ytregex.exec(embedUrl)[3];
            if (videoId) {
                embedUrl = `https://www.youtube.com/embed/${videoId}`;
            }
        }

        if (props.stimulus.data.toLowerCase().includes("vimeo")) {
            let vregex = new RegExp("(?:https?:)?(?:\/\/)?(?:www\.)?(?:vimeo)\.com(?:\/video)?\/([0-9]+)");
            let vmatch = embedUrl.match(vregex);
            console.log(vmatch);
            if (vmatch) {
                embedUrl = `https://player.vimeo.com/video/${vmatch[1]}`;
            }
        }
        ogMeta.value = {"video:url": embedUrl};
    }
    // console.log(ogMeta.value);
    getOpengraphData(props.stimulus.id, {refresh})
        .then((data) => {
            // console.log(data);
            if (!ogMeta.value) {
                ogMeta.value = data;
            } else {
                Object.assign(ogMeta.value, data);
            }
        })
        .catch((error) => {
            console.error(error);
            ogMeta.value = {
                error: error,
            };
        });
};
if (!ogMeta.value && ["url", "video"].includes(props.stimulus.type.slug)) {
    getOgMeta();
}
const activeIndex = ref(0);
const points = ref(props.points);
const activePoints = computed(() => {
    return props.points?.filter((point) => {
        return +point.pageIndex === +activeIndex.value;
    });
});
const annotation = computed(() => {
    if (props.annotation && +props.annotation.pageIndex === activeIndex.value) {
        return props.annotation;
    }
});
const pointRefs = useTemplateRef("pointRefs");
defineExpose({
    points,
    pointRefs,
    activeIndex,
    swiper: swiperEl,
});
const onSlideChange = (event) => {
    const [swiper] = event.detail;
    // console.log(swiper, swiper.realIndex);
    activeIndex.value = swiper.realIndex;
};

const prevSlide = () => {
    swiperEl.value.swiper.slidePrev();
};

const nextSlide = () => {
    swiperEl.value.swiper.slideNext();
};

const showHideAnnotations = () => {
    showAnnotations.value = !showAnnotations.value;
};

watch(fullWidth, (nVal) => {
    console.log(nVal);
    imageContainer.value.scrollIntoView({
        behavior: "smooth",
        block: "start",
    });
    // if (!nVal) {
    // } else {
    //     emit("fullwidth");
    // }
    // stimulusContainer.value.scrollIntoView({
    //     behavior: "smooth",
    //     block: "start",
    // });
});
</script>
<template>
    <div class="w-full max-w-full pt-1" ref="stimulusContainer">
        <p v-if="question?.param('allow_annotations')" class="-mb-6 text-sm">
            Long press or click and hold to anchor a comment to a place in the document.
        </p>
        <div class="sticky top-3 z-50 mb-2 flex items-end" v-if="['image', 'slides'].includes(stimulus.type.slug)">
            <div class="ml-auto flex items-center gap-2">
                <Button
                    v-if="points?.length"
                    type="button"
                    @click="showHideAnnotations"
                    text
                    class="flex items-center gap-1 !ring-0"
                    :class="[showAnnotations ? 'text-askr-green' : 'text-stone-500']"
                >
                    <!--                    <span class="text-xs">{{ showAnnotations ? "Hide" : "Show" }} annotations</span>-->
                    <Icon icon="mdi:comment" width="22" />
                </Button>
                <Button
                    type="button"
                    @click="fullWidth = !fullWidth"
                    text
                    :icon="fullWidth ? 'octicon:zoom-out-16' : 'octicon:zoom-in-16'"
                    class="hidden xl:block"
                    :class="[fullWidth ? 'text-stone-600 dark:text-stone-400' : 'text-askr-green']"
                />
                <div
                    class="pointer-events-none sticky top-10 z-50 flex justify-end gap-1"
                    v-if="['slides'].includes(stimulus.type.slug)"
                >
                    <button
                        type="button"
                        @click="prevSlide"
                        class="swiper-button-prev-custom pointer-events-auto -left-3 top-6 z-10 rounded-full bg-white/90 text-askr-green shadow-md shadow-black transition disabled:opacity-20"
                    >
                        <Icon icon="material-symbols:chevron-left-rounded" class="text-[1.675rem] lg:text-[2rem]" />
                    </button>
                    <button
                        type="button"
                        @click="nextSlide"
                        class="swiper-button-next-custom pointer-events-auto -right-3 top-6 z-10 rounded-full bg-white/90 text-askr-green shadow-md shadow-black transition disabled:opacity-20"
                    >
                        <Icon icon="material-symbols:chevron-right-rounded" class="text-[1.675rem] lg:text-[2rem]" />
                    </button>
                </div>
            </div>
        </div>
        <!--        <div v-if="stimulus.files?.[0]?.thumbnail" class="p-4">-->
        <!--            <span>has thumbnail:</span>-->
        <!--            <img-->
        <!--                v-if="stimulus.files[0].thumbnail"-->
        <!--                :src="stimulus.files[0].thumbnail"-->
        <!--                alt="Stimulus Image"-->
        <!--                class="aspect-video w-full object-cover rounded-lg"-->
        <!--            />-->
        <!--        </div>-->
        <template v-if="stimulus.type.slug === 'slides'">
            <div
                class="relative"
                :class="[{'h-screen': !fullWidth}, 'bg--stone-900 w-full max-w-full']"
                ref="imageContainer"
            >
                <swiper-container
                    :slides-per-view="1"
                    :navigation="{
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    }"
                    :pagination="true"
                    :loop="true"
                    :auto-heigh="true"
                    @swiperslidechange="onSlideChange"
                    pagination
                    ref="swiperEl"
                >
                    <template v-for="file in stimulus.files" :key="file.id">
                        <swiper-slide>
                            <div class="flex w-full max-w-full items-center">
                                <div class="relative" :class="[fullWidth ? 'w-full' : 'h-content']">
                                    <img
                                        :src="file.url"
                                        :alt="file.alt"
                                        class="rounded-md object-contain object-center"
                                        :class="[fullWidth ? 'h-auto w-full' : 'max-h-screen w-auto']"
                                    />
                                    <template v-if="showAnnotations">
                                        <template v-for="point in activePoints" :key="point.id">
                                            <span
                                                ref="pointRefs"
                                                :id="point.id"
                                                class="absolute z-10 -translate-x-2/4 -translate-y-2/4 cursor-pointer rounded-full bg-white/80 p-2 opacity-80"
                                                :style="{
                                                    top: point.y + '%',
                                                    left: point.x + '%',
                                                }"
                                                @click.stop="emit('point-click', point)"
                                            >
                                                <Icon
                                                    icon="mdi:comment"
                                                    width="30"
                                                    class="drop-shadow-[0_0_2px_rgb(0,0,0)]"
                                                    :class="[
                                                        {breathing: Str.of(point.focussed).toBool()},
                                                        `text-${point.color}`,
                                                    ]"
                                                />
                                            </span>
                                        </template>
                                        <span
                                            v-if="annotation"
                                            :id="annotation.id"
                                            class="absolute z-10 -translate-x-2/4 -translate-y-2/4 cursor-pointer rounded-full bg-white/80 p-2 opacity-80"
                                            :style="{
                                                top: annotation.y + '%',
                                                left: annotation.x + '%',
                                            }"
                                            @click.stop="emit('annotation-click', annotation)"
                                        >
                                            <Icon
                                                icon="mdi:comment"
                                                width="30"
                                                class="drop-shadow-[0_0_2px_rgb(0,0,0)]"
                                                :class="[
                                                    {breathing: Str.of(annotation.focussed).toBool()},
                                                    `text-${annotation.color}`,
                                                ]"
                                            />
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </swiper-slide>
                    </template>
                </swiper-container>
            </div>
        </template>
        <template v-if="stimulus.type.slug === 'image'">
            <div class="relative" :class="[{'h-screen': !fullWidth}]" ref="imageContainer">
                <div class="relative inline-block" :class="[fullWidth ? 'w-full' : 'h-content']">
                    <img
                        :src="stimulus.files?.[0]?.url"
                        alt="Stimulus Image"
                        class="rounded-lg"
                        :class="[fullWidth ? 'h-auto w-full' : 'max-h-screen w-auto']"
                    />
                    <template v-for="point in activePoints" :key="point.id" v-if="showAnnotations">
                        <span
                            ref="pointRefs"
                            :id="point.id"
                            class="absolute z-10 -translate-x-2/4 -translate-y-2/4 cursor-pointer rounded-full bg-white/80 p-2 opacity-80"
                            :style="{
                                top: point.y + '%',
                                left: point.x + '%',
                            }"
                            @click.stop="emit('point-click', point)"
                        >
                            <Icon
                                icon="mdi:comment"
                                width="30"
                                class="drop-shadow-[0_0_2px_rgb(255,255,255)]"
                                :class="[{breathing: Str.of(point.focussed).toBool()}, `text-${point.color}`]"
                            />
                        </span>
                    </template>
                    <span
                        v-if="annotation"
                        :id="annotation.id"
                        class="absolute z-10 -translate-x-2/4 -translate-y-2/4 cursor-pointer rounded-full bg-white/80 p-2 opacity-80"
                        :style="{
                            top: annotation.y + '%',
                            left: annotation.x + '%',
                        }"
                        @click.stop="emit('annotation-click', annotation)"
                    >
                        <Icon
                            icon="mdi:comment"
                            width="30"
                            class="drop-shadow-[0_0_2px_rgb(255,255,255)]"
                            :class="[{breathing: Str.of(annotation.focussed).toBool()}, `text-${annotation.color}`]"
                        />
                    </span>
                </div>
            </div>
        </template>
        <template v-if="['url', 'video'].indexOf(stimulus.type.slug) !== -1 && stimulus.data">
            <div class="relative flex w-full items-center justify-center">
                <PageLoading class="w-16 text-askr-green" v-if="getting.includes('opengraph') && !ogMeta" />
                <template v-if="ogMeta">
                    <template v-if="ogMeta.error">
                        {{ ogMeta.error.response?.data?.message ?? ogMeta.error.message ?? "An error occured" }}
                    </template>
                    <template v-else-if="ogMeta['video:url']">
                        <iframe
                            :src="ogMeta['video:url']"
                            class="aspect-video w-full rounded-lg"
                            allowfullscreen
                        ></iframe>
                    </template>
                    <template v-else-if="ogMeta?.image">
                        <a :href="stimulus.data" target="_blank" class="">
                            <img
                                :src="ogMeta.image"
                                alt="Stimulus Image"
                                class="h-auto w-full rounded-lg object-cover"
                            />
                        </a>
                    </template>
                    <template v-else>
                        <div
                            class="flex aspect-video w-full flex-col items-center justify-center rounded-lg bg-askr-green bg-default bg-cover text-white"
                        >
                            <Icon :icon="stimulus.type.icon" class="drop--shadow-lg --mt-6 h-12 w-12" />
                            <span>No preview available.</span>
                        </div>
                    </template>
                </template>
                <div class="absolute bottom-1 right-1 z-50 mt-1 flex justify-end">
                    <Button
                        icon="tabler:refresh"
                        :loading="getting.includes('opengraph')"
                        class="h-8 w-8 rounded-full bg-black text-white"
                        @click="getOgMeta(true)"
                    />
                </div>
            </div>
            <!--            {{ ogMeta }}-->
        </template>
        <template v-if="stimulus.type.slug === 'url' && stimulus.data">
            <a
                :href="stimulus.data"
                target="_blank"
                class="flex items-center justify-center gap-1 p-4 font-medium transition hover:bg-stone-50 hover:underline"
            >
                <span>{{ stimulus.data }}</span>
                <Icon icon="majesticons:open" class="text-stone-600" width="20" />
            </a>
        </template>
        <template v-if="stimulus.type.slug === 'video' && !stimulus.data">
            <iframe :src="stimulus.files?.[0]?.url" class="aspect-video w-full rounded-lg" allowfullscreen></iframe>
        </template>
        <template v-if="stimulus.type.slug === 'audio'">
            <div class="pt-4">
                <audio :src="stimulus.files?.[0]?.url" controls class="w-full rounded-t-lg"></audio>
            </div>
        </template>
        <template v-if="stimulus.type.slug === 'document' && stimulus.files?.length">
            <template v-if="pdfViewerEnabled">
                <iframe
                    :src="stimulus.files?.[0]?.url"
                    class="aspect-square h-auto w-full rounded-t-lg"
                    allowfullscreen
                />
            </template>
            <template v-else>
                <div class="flex justify-center pt-6">
                    <a
                        :href="stimulus.files?.[0]?.url"
                        target="_blank"
                        class="flex flex-col items-center rounded-lg p-6 font-bold transition hover:bg-stone-100"
                    >
                        <Icon :icon="stimulus.type.icon" class="text-stone-600" width="128" />
                        <span>Click here to download</span>
                    </a>
                </div>
            </template>
        </template>
    </div>
</template>
<style scoped>
.swiper-wrapper {
    align-items: center; /* add this will all relevant prefixes */
}

.breathing {
    animation: breathing 0.25s ease-out infinite normal;
}

.shake {
    animation: shake 1s infinite;
}

@keyframes breathing {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.2);
    }

    100% {
        transform: scale(1);
    }
}

@keyframes shake {
    0% {
        transform: translate(1px, 1px);
    }
    10% {
        transform: translate(-1px, -2px);
    }
    20% {
        transform: translate(-3px, 0px);
    }
    30% {
        transform: translate(3px, 2px);
    }
    40% {
        transform: translate(1px, -1px);
    }
    50% {
        transform: translate(-1px, 2px);
    }
    60% {
        transform: translate(-3px, 1px);
    }
    70% {
        transform: translate(3px, 1px);
    }
    80% {
        transform: translate(-1px, -1px);
    }
    90% {
        transform: translate(1px, 2px);
    }
    100% {
        transform: translate(1px, -2px);
    }
}
</style>
